import {
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { DocumentsEditorCardComponent } from '@barthauer/abc/common-editors';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { BasysMapFrameService } from '../basys-map-frame.service';
import { PanelComponent } from '../shared/panel.component';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class DocumentsComponent extends PanelComponent implements OnInit, OnDestroy {
  activeObjectInfo: DataEntity;
  scrollViewPortElementName: string = 'documentList';
  isClickFromTable: boolean = false;
  private _selected: DocumentTypeDefinition;
  private documentEditorComponent: DocumentsEditorCardComponent;
  private documentEditorRef: ComponentRef<any>;
  @ViewChild('dtSelection', { static: false }) dtSelection: MatSelect;
  @ViewChild('dynamic', { static: false, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  public res = ResourceSet.Empty;
  private resAsync: Promise<ResourceSet>;

  constructor(
    public frame: BasysMapFrameService,
    private abcCore: AbcCore,
    private resourceService: ResourceManagerService,
  ) {
    super();
    super.init(frame);
    this.resAsync = this.resourceService.getResourceSet('basys-map-frame');
    this.subscriptions.push(
      this.frame.lib.getShowDocumentClickSource().subscribe((clickSource) => {
        this.isClickFromTable = clickSource;
      }),
    );
    this.subscriptions.push(
      this.frame.lib.getDocumentsData().subscribe(
        async (objectInfos) => {
          if (objectInfos?.length) {
            await this.setActiveObjectInfo(objectInfos[0]);
          }
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );
  }

  private async setActiveObjectInfo(objectInfo: DataEntity): Promise<void> {
    if (this.activeObjectInfo?.id == objectInfo?.id) {
      return;
    }
    this.activeObjectInfo = objectInfo;
    this._currentDocumentTypes = [];
    const res = await this.resAsync;
    const defaultDocumentsDefinitions: DocumentTypeDefinition = {
      objectType: this.activeObjectInfo.objectType,
      filter: 'ParentId=$ObjectId',
      displayText: res.get('Documents-Title'),
    };
    const mtObjectDocumentsDefinitions: DocumentTypeDefinition = {
      objectType: 'MtObject',
      filter: 'ParentId In (Type(MtObject)[ParentId=$CurrentMunicipalityVariantId And IdMasterObject=$ObjectId].Id)',
      displayText: res.get('DocumentsMtObject-Title'),
    };
    const baulosDocumentsDefinitions: DocumentTypeDefinition = {
      objectType: 'Baulos',
      filter: 'ParentId In (Type(BaulosZuordnung)[ParentId=$ObjectId].IdRefBaulos)',
      displayText: res.get('DocumentsBaulos-Title'),
    };
    const gemeindeDocumentsDefinitions: DocumentTypeDefinition = {
      objectType: 'Gemeinde',
      filter: 'Id In (Type(ObjectDocument)[ParentId=$ObjectId].IdRefDocument)',
      displayText: res.get('DocumentsGemeinde-Title'),
    };
    if (this.frame.lib.preferMtObjectDocuments) {
      await this.tryAddDocumentType(mtObjectDocumentsDefinitions);
    }

    await this.tryAddDocumentType(defaultDocumentsDefinitions);
    await this.tryAddDocumentType(baulosDocumentsDefinitions);
    await this.tryAddDocumentType(gemeindeDocumentsDefinitions);

    if (!this.frame.lib.preferMtObjectDocuments) {
      await this.tryAddDocumentType(mtObjectDocumentsDefinitions);
    }

    if (this._currentDocumentTypes.length === 0) {
      this._currentDocumentTypes.push(defaultDocumentsDefinitions);
    }

    this.selected = this._currentDocumentTypes[0];
    this.dtSelection.placeholder = this.selected.displayText;
  }

  private async tryAddDocumentType(documentTypeDefinition: DocumentTypeDefinition): Promise<boolean> {
    const ot = await this.abcCore.objectSpace.getObjectType(documentTypeDefinition.objectType).toPromise();
    if (ot) {
      if (documentTypeDefinition.filter) {
        const result = ConvertHelper.toBoolean(
          await this.abcCore.data
            .executeReadValueQuery({
              OPath: `Exists(Type(Document)[${documentTypeDefinition.filter}])`,
              Parameters: [{ Name: 'ObjectId', Value: this.activeObjectInfo?.id }],
            })
            .toPromise(),
        );
        if (result) {
          this._currentDocumentTypes.push(documentTypeDefinition);
          return true;
        }
      }
    }
    return false;
  }

  ngOnDestroy(): void {
    console.log('DocumentsComponent ngOnDestroy');
    super.ngOnDestroy();
  }

  async ngOnInit() {
    super.ngOnInit();
    this.res = await this.resAsync;
  }

  private initDocumentEditor() {
    this.documentEditorRef = this.frame.loadComponent(DocumentsEditorCardComponent, this.viewContainerRef);
    this.documentEditorComponent = <DocumentsEditorCardComponent>this.documentEditorRef.instance;
  }

  public returnToTable(): void {
    this.frame.lib.openBottomSheetByName('Table');
  }

  private _currentDocumentTypes: DocumentTypeDefinition[];
  public get currentDocumentTypes(): DocumentTypeDefinition[] {
    return this._currentDocumentTypes ?? [];
  }

  public get selected(): DocumentTypeDefinition {
    return this._selected;
  }

  public set selected(value: DocumentTypeDefinition) {
    if (this._selected !== value) {
      this._selected = value;
      if (!this.documentEditorComponent) {
        this.initDocumentEditor();
      }
      this.documentEditorComponent.setParentIdAndFilter(this.activeObjectInfo.id, this.selected?.filter);
    }
  }
}

class DocumentTypeDefinition {
  objectType: string | number;
  filter: string;
  displayText: string;
}
